import { useSpecs } from '@common/graph/gem-specs';
import { useChangeGraphPath } from '@common/historyHooks';
import { SubGraphContainer, SubGraphLabel, SubGraphRoot, useVirtualPorts } from '@common/subgraph/graph';
import { GraphBreadcrumb } from '@common/subgraph/GraphBreadcrumb';
import { useCurrentGraph } from '@common/useOriginalGraph';
import { filterAutoPositionedNodes } from '@common/utils/graph/layout';
import { MinimizeAIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { Button, StackItem } from '@prophecy/ui-v3';
import { defaultEdgeTypes, defaultNodeTypes } from '@prophecy/ui-v3/Graph/constants';
import { Graph } from '@prophecy/ui-v3/Graph/Graph';
import { GraphScrollView } from '@prophecy/ui-v3/Graph/GraphScrollView';
import { useBoundary, useRestoreOrFitView } from '@prophecy/ui-v3/Graph/hooks';
import { getLayoutedElements } from '@prophecy/ui-v3/Graph/layout';
import { Boundary, EdgeData, NodeData, SimplePipelineNodeData, StrippedNodeData } from '@prophecy/ui-v3/Graph/types';
import { isNodeNotPositioned } from '@prophecy/ui-v3/Graph/utils';
import { Stack } from '@prophecy/ui-v3/Layout';
import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { getGraphProperty, getProcessesPath, isSubGraphOrMetaGem } from 'frontend/core/src/Parser/bindings';
import { Edge, Node, OnNodesChange, OnEdgesChange, useNodesState, useEdgesState, useStore } from 'reactflow';

import { subgraphNodes } from '../../Workflow/GraphView';
import { LineageGraph } from '../types';
import { useGraph, useProcessSelect } from './hook';

export function Canvas() {
  const {
    rootGraph: lineageGraph,
    rootPath,
    graphPath
  } = useCurrentGraph(({ rootGraph, rootPath, graphPath }) => {
    return { rootGraph: rootGraph as LineageGraph, rootPath, graphPath };
  });

  return <GraphView key={graphPath} rootPath={rootPath} lineageGraph={lineageGraph} graphPath={graphPath} />;
}

function GraphView({
  lineageGraph,
  rootPath,
  graphPath
}: {
  lineageGraph: LineageGraph;
  rootPath: string;
  graphPath: string;
}) {
  const specs = useSpecs();
  const [nodes, setNodes, onNodesChange] = useNodesState<SimplePipelineNodeData>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<EdgeData>([]);
  const showSubgraph = rootPath !== graphPath;

  const graph = showSubgraph ? (getGraphProperty(lineageGraph, graphPath, rootPath) as LineageGraph) : lineageGraph;
  const onProcessSelect = useProcessSelect(graphPath);

  const changeGraphPath = useChangeGraphPath();
  function onSubGraphClose() {
    changeGraphPath(rootPath);
  }
  function openSubGraph(path: string) {
    changeGraphPath(path);
  }
  const { onLayout } = useGraph(lineageGraph, graph, graphPath, setNodes, setEdges);
  const handleAutoLayout = usePersistentCallback(async () => {
    const layoutedNodes = await getLayoutedElements(nodes as unknown as Node<NodeData>[], edges);
    return onLayout?.(layoutedNodes, { showPrompt: false });
  });
  /**
   * If any of the node is not positioned, all are MaxInt or undefined.
   * Or if all the element are placed at 100, 100 (old transpiler case)
   * treat them as non layouted
   */
  const hasUnLayoutedNodes =
    nodes.some((node) => isNodeNotPositioned(node as unknown as Node<NodeData>)) ||
    (nodes.length > 1 && nodes.every((node) => node.position.x === 100 || node.position.y === 100));

  const { initializeBoundary, boundary } = useBoundary(
    nodes.filter((node) => filterAutoPositionedNodes(node as unknown as Node<StrippedNodeData>)),
    graphPath
  );
  const { restoreOrFitView } = useRestoreOrFitView({ filter: filterAutoPositionedNodes, graphId: graphPath });
  const onNodeClick = function onNodeClick(event: React.MouseEvent, element: Node) {
    const processId = element.id;
    const process = graph.processes[processId];
    if (isSubGraphOrMetaGem(process, specs)) {
      openSubGraph(`${getProcessesPath(graphPath)}.${processId}`);
    } else {
      onProcessSelect(processId);
    }
  };
  const onInit = () => {
    if (hasUnLayoutedNodes) {
      handleAutoLayout();
    }
    restoreOrFitView();
    initializeBoundary();
  };

  if (nodes.length < 1) {
    return null;
  }

  return (
    <GraphScrollView boundary={boundary} isSubgraph={showSubgraph}>
      <>
        {showSubgraph ? (
          <SubGraph
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeClick={onNodeClick}
            onInit={onInit}
            lineageGraph={lineageGraph}
            graph={graph}
            graphPath={graphPath}
            initializeBoundary={initializeBoundary}
            boundary={boundary}
            openSubGraph={openSubGraph}
            closeSubGraph={onSubGraphClose}
          />
        ) : (
          <Graph
            translateExtent={boundary}
            nodeTypes={defaultNodeTypes}
            edgeTypes={defaultEdgeTypes}
            selectionKeyCode='-1'
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeClick={onNodeClick}
            onInit={onInit}
            readOnly={true}
            height={'100%'}
            width={'100%'}
          />
        )}
      </>
    </GraphScrollView>
  );
}

function SubGraph({
  nodes: _nodes,
  edges,
  graph,
  onNodesChange,
  onEdgesChange,
  lineageGraph,
  graphPath,
  boundary,
  openSubGraph,
  closeSubGraph,
  onInit,
  onNodeClick
}: {
  nodes: Node<SimplePipelineNodeData>[];
  edges: Edge<EdgeData>[];
  graph: LineageGraph;
  onNodesChange: OnNodesChange;
  onEdgesChange: OnEdgesChange;
  lineageGraph: LineageGraph;
  graphPath: string;
  openSubGraph: (id: string) => void;
  closeSubGraph: () => void;
  initializeBoundary: () => void;
  boundary: Boundary;
  onInit: () => void;
  onNodeClick: (event: React.MouseEvent, element: Node) => void;
}) {
  const { nodes, subGraphEl } = useVirtualPorts(graph, _nodes);
  const addSelectedEdges = useStore((store) => store.addSelectedEdges);

  return (
    <Stack height='100%' gap={theme.spaces.x6}>
      <GraphBreadcrumb
        graphPath={graphPath}
        graph={graph}
        workflow={lineageGraph}
        openSubGraph={openSubGraph}
        readOnly={true}
        addSelectedNodes={addSelectedEdges}
      />
      <StackItem grow='1'>
        <SubGraphRoot>
          <Stack direction='horizontal' align='end'>
            <Button
              onClick={closeSubGraph}
              variant='secondaryGray'
              size='s'
              icon={<MinimizeAIcon type='default' />}
              aria-label='Minimize subgraph'
              data-test-id='lineage-pipeline-canvas-minimize-button'
            />
          </Stack>

          <SubGraphContainer ref={subGraphEl}>
            <Graph
              translateExtent={boundary}
              nodeTypes={subgraphNodes}
              edgeTypes={defaultEdgeTypes}
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onNodeClick={onNodeClick}
              onInit={onInit}
              readOnly={true}
              filterNodes={filterAutoPositionedNodes}
              height={'100%'}
              width={'100%'}
            />
          </SubGraphContainer>
        </SubGraphRoot>
      </StackItem>
      <SubGraphLabel level='sm'>Subgraph</SubGraphLabel>
    </Stack>
  );
}
